import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Callout, Divider, EmptyStateDefaultIllustration, IconButton, LinearProgress, List, ListItem } from "@octopusdeploy/design-system-components";
import { MinusCircleIcon, PlusCircleIcon } from "@octopusdeploy/design-system-icons";
import { colorScales, space, text, borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { GitHubAppConnectionResource, GitHubRepository, PagedGitHubAppRepositories } from "@octopusdeploy/octopus-server-client";
import _ from "lodash";
import pluralize from "pluralize";
import * as React from "react";
import { useState } from "react";
import { Action } from "~/analytics/Analytics";
import { VerticalDivider } from "~/areas/tenants/components/HeaderBar/CollapsibleFilter";
import { repository } from "~/clientInstance";
import { ListItemContentWithButtons } from "~/components/BorderedListItem";
import BusyFromPromise from "~/components/BusyFromPromise";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent";
import { getErrorsFromError, isErrors } from "~/components/DataBaseComponent/Errors";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import ErrorPanel from "~/components/ErrorPanel";
import FilterSearchBox from "~/components/FilterSearchBox";
import { useTrackedGitConnectionEvent } from "./Analytics/useTrackedGitConnectionEvent";
import { GitHubRepositoryVisibilityChip } from "./ChipList/GitHubChips";
interface PanelRepositorySelectorProps {
    connection: GitHubAppConnectionResource;
    closeDialog: () => void;
    refreshPage: () => Promise<boolean>;
}
export default function PanelRepositorySelector({ connection, closeDialog, refreshPage }: PanelRepositorySelectorProps) {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchResult, setSearchResult] = useState<PagedGitHubAppRepositories>();
    const [showNoneSelectedBanner, setShowNoneSelectedBanner] = useState<boolean>(false);
    const [selectedRepositoriesToDisplay, setSelectedRepositoriesToDisplay] = useState<GitHubRepository[]>(connection.Repositories);
    const [repositoriesToAdd, setRepositoriesToAdd] = useState<GitHubRepository[]>([]);
    const [repositoriesToRemove, setRepositoriesToRemove] = useState<GitHubRepository[]>([]);
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const trackAction = useTrackedGitConnectionEvent();
    function repositoryIsOnConnection(repo: GitHubRepository, connectedRepos: GitHubRepository[]): boolean {
        return connectedRepos.some((r) => r.RepositoryId === repo.RepositoryId);
    }
    const handleSearchResultClicked = (item: GitHubRepository) => {
        if (!repositoryIsOnConnection(item, connection.Repositories)) {
            setRepositoriesToAdd((r) => [...r, item]); // add to add list
        }
        setRepositoriesToRemove((r) => r.filter((repo) => repo.RepositoryId !== item.RepositoryId)); // clean from remove list
        setSelectedRepositoriesToDisplay((r) => _.uniqBy([...r, item], "RepositoryId")); // add to the display list
        setShowNoneSelectedBanner(false);
    };
    const handleSelectedRepositoryClicked = (item: GitHubRepository) => {
        if (repositoryIsOnConnection(item, connection.Repositories)) {
            setRepositoriesToRemove((r) => [...r, item]); // add to remove list
        }
        else {
            setRepositoriesToAdd((r) => r.filter((repo) => repo.RepositoryId !== item.RepositoryId)); // clean from add list
            setSelectedRepositoriesToDisplay((r) => r.filter((repo) => repo.RepositoryId !== item.RepositoryId)); // clean from the display list
        }
    };
    useDoBusyTaskEffect(doBusyTask, async () => {
        const result = await repository.GitHubApp.searchRepositories(connection.Installation.AccountLogin, searchTerm, 0, 30);
        setSearchResult(result);
    }, [connection.Installation.AccountLogin, searchTerm]);
    async function handleSave() {
        await doBusyTask(async () => {
            await trackAction("Save GitHub connection in modal", Action.Save, async () => {
                const repositoriesListWithAdditions = [...connection.Repositories, ...repositoriesToAdd];
                const repositoryIdsToRemove = new Set(repositoriesToRemove.map((r) => r.RepositoryId));
                const repositoriesListWithRemovals = repositoriesListWithAdditions.filter((repo) => !repositoryIdsToRemove.has(repo.RepositoryId));
                const repositoryIdsToSave = _.uniq(repositoriesListWithRemovals.map((repo) => repo.RepositoryId));
                if (_.isEmpty(repositoryIdsToSave)) {
                    setShowNoneSelectedBanner(true);
                }
                await repository.GitHubApp.modifyConnection(connection.Id, { RepositoryIds: repositoryIdsToSave });
            });
        }, {
            onSuccess: async () => {
                closeDialog();
                await refreshPage();
            },
        });
    }
    const actions = [
        <ActionButton key="Cancel" label="Cancel" type={ActionButtonType.Secondary} keyboardFocused={true} onClick={() => closeDialog()}/>,
        <ActionButton key="Save" label="Save" type={ActionButtonType.Primary} onClick={() => {
                handleSave();
            }}/>,
    ];
    const styles = {
        container: css({
            display: "flex",
            flexDirection: "column",
            gap: space[8],
            height: "600px",
        }),
        panels: css({
            flex: 1,
            minHeight: 0,
            gap: space[8],
            paddingLeft: space[8],
            paddingRight: space[8],
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            backgroundColor: themeTokens.color.background.secondary.default,
        }),
        panel: css({
            border: `1px solid ${themeTokens.color.border.primary}`,
            borderRadius: borderRadius.small,
            backgroundColor: themeTokens.color.background.primary.default,
            overflowY: "scroll",
        }),
        actions: css({
            display: "flex",
            justifyContent: "flex-end",
            gap: space[8],
            paddingLeft: space[8],
            paddingRight: space[8],
            paddingBottom: space[8],
        }),
        emptySelectedList: css({
            color: themeTokens.color.text.tertiary,
            font: text.body.regular.medium,
            height: "100%",
            display: "flex",
            gap: space[16],
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
        }),
        emptyStateIllustrationWrapper: css({
            width: "20rem",
        }),
    };
    const convertedErrors = doBusyTaskStatus.errors === undefined ? undefined : isErrors(doBusyTaskStatus.errors) ? [doBusyTaskStatus.errors] : doBusyTaskStatus.errors.map(getErrorsFromError);
    return (<div className={styles.container}>
            <div>
                <BusyFromPromise promise={doBusyTaskStatus.isInProgress}>{(isBusy: boolean) => <LinearProgress variant={"indeterminate"} show={isBusy}/>}</BusyFromPromise>
                {convertedErrors &&
            convertedErrors.map((error, index) => (<ErrorPanel key={index} errors={error.errors} message={error.message} parsedHelpLinks={error.parsedHelpLinks} helpText={error.helpText} helpLink={error.helpLink} statusCode={error.statusCode}/>))}
            </div>
            <div className={styles.panels}>
                <div className={styles.panel}>
                    <ListHeader title="Add repositories" accessory={<FilterSearchBox value={searchTerm} onChange={setSearchTerm} debounceDelay={600} placeholder={`Search by name...`}/>}/>
                    {searchResult && (<List items={searchResult.Repositories} rowKey={(x) => x.RepositoryId} renderRow={({ item }) => (<RepositoryListItem repository={item} handleSearchResultClicked={handleSearchResultClicked} handleSelectedRepositoryClicked={handleSelectedRepositoryClicked} panel="AllRepositories" selectedRepositoriesToDisplay={selectedRepositoriesToDisplay} repositoriesToRemove={repositoriesToRemove}/>)} empty={<NoAvailableItemsLabel count={searchResult.TotalResults}/>}/>)}
                </div>
                <div className={styles.panel}>
                    <ListHeader title="Selected repositories" subtitle={`${pluralize("repository", selectedRepositoriesToDisplay.length, true)} selected`}/>
                    {showNoneSelectedBanner && (<Callout title="No repositories selected" type="information">
                            Please add at least one repository to connect or disconnect this account.
                        </Callout>)}
                    <List items={selectedRepositoriesToDisplay} rowKey={(x) => x.RepositoryId} renderRow={({ item }) => (<RepositoryListItem repository={item} handleSearchResultClicked={handleSearchResultClicked} handleSelectedRepositoryClicked={handleSelectedRepositoryClicked} panel="SelectedRepositories" selectedRepositoriesToDisplay={selectedRepositoriesToDisplay} repositoriesToRemove={repositoriesToRemove}/>)} empty={<div className={styles.emptySelectedList}>
                                <div className={styles.emptyStateIllustrationWrapper}>
                                    <EmptyStateDefaultIllustration />
                                </div>
                                Looks like you haven't selected any {pluralize("repository")} yet.
                            </div>}/>
                </div>
            </div>
            <div className={styles.actions}>{actions}</div>
        </div>);
}
interface ListHeaderProps {
    title: string;
    accessory?: React.ReactNode;
    subtitle?: string;
}
function ListHeader({ title, accessory, subtitle }: ListHeaderProps) {
    const styles = {
        container: css({
            padding: space[16],
        }),
        titleContainer: css({
            display: "flex",
            alignItems: "center",
            gap: space[16],
        }),
        title: css({
            font: text.body.bold.large,
            color: themeTokens.color.text.primary,
        }),
        subtitle: css({
            font: text.body.regular.small,
            color: themeTokens.color.text.secondary,
        }),
    };
    return (<>
            <div className={styles.container}>
                <div className={styles.titleContainer}>
                    <div className={styles.title}>{title}</div>
                    {accessory ? <VerticalDivider /> : null}
                    {accessory}
                </div>
                {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
            </div>
            <Divider />
        </>);
}
function NoAvailableItemsLabel({ count }: {
    count: number;
}) {
    return <div className={styles.emptyUnselectedList}>{count > 0 ? `All available ${pluralize("repository")} have been selected.` : `All available ${pluralize("repository")} have already been connected.`}</div>;
}
type Panel = "AllRepositories" | "SelectedRepositories";
interface RepositoryListItemProps {
    repository: GitHubRepository;
    handleSearchResultClicked: (repository: GitHubRepository) => void;
    handleSelectedRepositoryClicked: (repository: GitHubRepository) => void;
    panel: Panel;
    selectedRepositoriesToDisplay: GitHubRepository[];
    repositoriesToRemove: GitHubRepository[];
}
function RepositoryListItem({ repository, handleSearchResultClicked, handleSelectedRepositoryClicked, panel, selectedRepositoriesToDisplay, repositoriesToRemove }: RepositoryListItemProps) {
    const styles = {
        title: css({
            font: text.body.regular.large,
            color: themeTokens.color.text.primary,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
        }),
        subtitle: css({
            font: text.body.regular.medium,
            color: themeTokens.color.text.secondary,
        }),
    };
    const selectedRepositoriesToDisplayIds = React.useMemo(() => new Set(selectedRepositoriesToDisplay.map((r) => r.RepositoryId)), [selectedRepositoriesToDisplay]);
    const repositoriesToRemoveIds = React.useMemo(() => new Set(repositoriesToRemove.map((r) => r.RepositoryId)), [repositoriesToRemove]);
    let button = null;
    if (panel === "AllRepositories") {
        if (selectedRepositoriesToDisplayIds.has(repository.RepositoryId) && !repositoriesToRemoveIds.has(repository.RepositoryId)) {
            button = <IconButton customIcon={<DeactivatedAddIcon />} disabled={true}/>;
        }
        else {
            button = <IconButton customIcon={<AddIcon />} onClick={() => handleSearchResultClicked(repository)}/>;
        }
    }
    else {
        if (repositoriesToRemoveIds.has(repository.RepositoryId)) {
            button = <IconButton customIcon={<DeactivatedAddIcon />} onClick={() => handleSearchResultClicked(repository)}/>;
        }
        else {
            button = <IconButton customIcon={<RemoveIcon />} onClick={() => handleSelectedRepositoryClicked(repository)}/>;
        }
    }
    return (<ListItem>
            <ListItemContentWithButtons content={<>
                        <div className={styles.title}>
                            <span>{repository.RepositoryName}</span>
                            <GitHubRepositoryVisibilityChip repository={repository}/>
                        </div>
                        <div className={styles.subtitle}>Description</div>
                    </>} buttons={button}/>
        </ListItem>);
}
function RemoveIcon() {
    return <MinusCircleIcon size={24} color={colorScales.red[500]}/>;
}
function AddIcon() {
    return <PlusCircleIcon size={24} color={colorScales.blue[500]}/>;
}
function DeactivatedAddIcon() {
    return <PlusCircleIcon size={24} color={colorScales.grey[500]}/>;
}
const styles = {
    emptyUnselectedList: css({
        paddingTop: space[8],
        color: themeTokens.color.text.tertiary,
        font: text.body.regular.medium,
    }),
};
